/**
 * Unified Auth Provider
 *
 * Provides authentication that works seamlessly for both web (Firebase) and native (Flutter bridge).
 * - Web: Uses Firebase Auth directly (OAuth, email/password)
 * - Native: Calls native auth methods via bridge and syncs with Firebase
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  User as FirebaseUser,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  getIdToken,
  sendPasswordResetEmail,
  sendEmailVerification,
} from "firebase/auth";
import { useQueryClient } from "@tanstack/react-query";
import { bridge, BridgeChannels, BridgeActions } from "../bridge";
import { api, setAuthTokenGetter } from "../api";
import type { LoginEventResponse } from "../api/useLoginEvent";
import {
  getFirebaseAuth,
  signInToFirebaseWithCustomToken,
  signOutFromFirebase,
} from "../firebase";
import {
  SignInDialogProvider,
  SignInDialogContext,
  type SignInOptions,
} from "./SignInDialogContext";
import { SignInDialog } from "../../components/SignInDialog";
import { useLocation } from "wouter";

// ============================================================================
// Types
// ============================================================================

// User object shape that works for both platforms
export interface AuthUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  imageUrl: string | null;
}

// Auth actions interface (for components that need to call auth methods directly)
export interface AuthActions {
  signInWithGoogle: () => Promise<AuthUser | null>;
  signInWithApple: () => Promise<AuthUser | null>;
  signInWithEmail: (email: string, password: string) => Promise<AuthUser | null>;
  signUpWithEmail: (email: string, password: string, firstName: string, lastName: string) => Promise<AuthUser | null>;
  resetPassword: (email: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

export interface AuthContextValue {
  /** Whether the auth state has been loaded */
  isLoaded: boolean;

  /** Whether the user is signed in */
  isSignedIn: boolean;

  /** The current user ID */
  userId: string | null;

  /** The current user object (if signed in) */
  user: AuthUser | null;

  /** Sign out the current user */
  signOut: () => Promise<void>;

  /** Get the current auth token */
  getToken: () => Promise<string | null>;

  /** Whether running in native context */
  isNative: boolean;

  /** Show native account dialog (only works on native) */
  showAccountDialog: () => Promise<void>;

  /** Open the sign-in dialog */
  openSignIn: (options?: SignInOptions) => void;

  /** Close the sign-in dialog */
  closeSignIn: () => void;

  /** Whether the sign-in dialog is open */
  isSignInOpen: boolean;
}

// ============================================================================
// Contexts
// ============================================================================

// Persist contexts across hot reloads to prevent "useAuth must be used within AuthProvider" errors
// During HMR, module-level createContext() creates a new object, but consumers may still
// reference the old one. Storing on globalThis ensures the same context object is reused.
const getOrCreateContext = <T,>(key: string, defaultValue: T): React.Context<T> => {
  const globalKey = `__${key}__` as keyof typeof globalThis;
  if (!(globalKey in globalThis)) {
    (globalThis as any)[globalKey] = createContext<T>(defaultValue);
  }
  return (globalThis as any)[globalKey];
};

export const AuthContext = getOrCreateContext<AuthContextValue | null>('AuthContext', null);

// Auth actions context (for direct auth method access, e.g., Migrate page)
export const AuthActionsContext = getOrCreateContext<AuthActions | null>('AuthActionsContext', null);

// Legacy alias for backwards compatibility
export const FirebaseAuthActionsContext = AuthActionsContext;

// ============================================================================
// Helpers
// ============================================================================

// Convert Firebase User to AuthUser
function firebaseUserToAuthUser(user: FirebaseUser): AuthUser {
  const displayNameParts = user.displayName?.split(" ") ?? [];
  return {
    id: user.uid,
    firstName: displayNameParts[0] ?? null,
    lastName: displayNameParts.slice(1).join(" ") || null,
    email: user.email,
    imageUrl: user.photoURL,
  };
}

const signInFromNativeAuthToken = async (token: string) => {
  const customToken = await api.get<{ token: string }>(
    "/user/auth/token",
    undefined,
    async () => token
  );
  if (customToken.success && customToken.data?.token) {
    return await signInToFirebaseWithCustomToken(customToken.data.token);
  }
};

// ============================================================================
// Inner Auth Provider (needs SignInDialogContext)
// ============================================================================

interface InnerAuthProviderProps {
  children: React.ReactNode;
}

function InnerAuthProvider({ children }: InnerAuthProviderProps) {
  const isNative = bridge.isNative;
  const dialogContext = useContext(SignInDialogContext);

  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prevUserRef = useRef<FirebaseUser | null | undefined>(undefined);

  const auth = getFirebaseAuth();
  const queryClient = useQueryClient();
  const [,setLocation] = useLocation();

  // Listen for auth state changes from Firebase
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Detect if user just signed in (was null/undefined, now has user)
      const wasNotSignedIn = prevUserRef.current === null;
      const isNowSignedIn = firebaseUser !== null;
      const isInitialCheck = prevUserRef.current === undefined;
      const justSignedIn = !isInitialCheck && wasNotSignedIn && isNowSignedIn;
      // Sign-out transition: was a real user, now null. Drop all user-scoped
      // cache exactly once here — not on every signed-out render — so stale
      // private data can't leak across sessions without nuking public-page
      // caches on each mount.
      const justSignedOut = prevUserRef.current != null && firebaseUser === null;

      // Update refs and state
      prevUserRef.current = firebaseUser;
      setUser(firebaseUser);
      setIsLoaded(true);

      if (justSignedOut) {
        queryClient.clear();
      }

      if (justSignedIn && dialogContext && firebaseUser) {
        try {
          // Block dialog dismissal on login event; use token from current user so request is authenticated before state updates
          const tokenGetter = () => getIdToken(firebaseUser);
          const platform = bridge.isNative ? (bridge.platform || "native") : "web";
          const result = (await api.post<LoginEventResponse>(
            "/user/login-event",
            { platform },
            tokenGetter
          )) as LoginEventResponse;
          const quizCompleted = result?.quizCompleted === true;
          const wasLoginNotSignup = dialogContext.options.asSignUp !== true;

          if (wasLoginNotSignup && !quizCompleted) {
            dialogContext.setView("account-not-found");
          } else {
            // Signup flow: still close and let app redirect to quiz
            dialogContext.options.onSuccess?.();
            dialogContext.forceClose();
          }
        } catch (e) {
          console.error("[AuthProvider] Login event failed", e);
          // On error, close dialog and call onSuccess so user isn’t stuck
          dialogContext.options.onSuccess?.();
          dialogContext.forceClose();
        }
      }
    });

    return unsubscribe;
  }, [auth, dialogContext, queryClient]);

  // Listen for auth credentials from native bridge (custom token sign-in)
  useEffect(() => {
    if (!isNative) return;

    const unsubscribe = bridge.on(
      BridgeChannels.AUTH,
      BridgeActions.STATE_CHANGED,
      async (payload: {
        userId: string | null;
        token: string | null;
        user: AuthUser | null;
      }) => {
        // Skip re-auth if already signed in as the same user — the native bridge
        // fires STATE_CHANGED on every tap, and calling signInWithCustomToken when
        // already authenticated causes a brief null→user auth state cycle that
        // unmounts/remounts components (visible as particle flicker, etc.).
        const currentUid = getFirebaseAuth().currentUser?.uid ?? null;
        if (payload.userId && currentUid === payload.userId) return;

        console.log("[AuthProvider] Native auth state changed:", JSON.stringify(payload, null, 2));
        if (payload.token) {
          await signInFromNativeAuthToken(payload.token);
        } else {
          await signOutFromFirebase();
        }
      }
    );

    return unsubscribe;
  }, [isNative]);

  // Initial native auth sync - get token from native and sign in to Firebase
  useEffect(() => {
    if (!isNative) return;
    async function syncNativeAuth() {
      try {
        const token = await bridge.request<string | null>(
          BridgeChannels.AUTH,
          BridgeActions.GET_TOKEN
        );
        if (token) {
          await signInFromNativeAuthToken(token);
        }
      } catch (e) {
        console.error("[AuthProvider] Failed to sync native auth:", e);
      }
    }
    syncNativeAuth();
  }, [isNative]);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (!user) return null;
    try {
      return await getIdToken(user);
    } catch (e) {
      console.error("[AuthProvider] getToken error:", e);
      return null;
    }
  }, [user]);
  setAuthTokenGetter(getToken);

  const handleSignOut = useCallback(async () => {
    try {
      if (isNative) {
        await bridge.request(BridgeChannels.AUTH, BridgeActions.SIGN_OUT);
      }
      await firebaseSignOut(auth);
      setLocation("/");
    } catch (e) {
      console.error("[AuthProvider] Sign out error:", e);
    }
  }, [auth, isNative, setLocation]);

  // Native auth via bridge - supports 'google', 'apple', 'email' methods
  // Returns AuthUser on success, null on failure/cancellation
  const nativeAuth = useCallback(
    async (
      method: "google" | "apple" | "email",
      credentials?: { email: string; password: string }
    ): Promise<AuthUser | null> => {
      if (!isNative) {
        setError("Not running in native context");
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await bridge.request<{
          success: boolean;
          userId?: string;
          token?: string;
          error?: string;
        }>(
          BridgeChannels.AUTH,
          BridgeActions.NATIVE_AUTH,
          { method, credentials },
          60000 * 2 // 2 minute timeout for auth
        );

        if (result.success && result.token) {
          // Sign in to Firebase with the token from native
          const firebaseUser = await signInFromNativeAuthToken(result.token);
          if (firebaseUser) {
            return firebaseUserToAuthUser(firebaseUser);
          }
          return null;
        } else {
          const errorMsg = result.error || "Authentication failed";
          if (errorMsg !== "cancelled") {
            setError(errorMsg);
          }
          return null;
        }
      } catch (e: any) {
        const errorMsg = e.message || "Authentication failed";
        setError(errorMsg);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [isNative]
  );

  // Auth methods - work for both web and native
  // Returns AuthUser on success, null on failure/cancellation
  const signInWithGoogle = useCallback(async (): Promise<AuthUser | null> => {
    if (isNative) {
      // Use native bridge for Google SSO
      return await nativeAuth("google");
    }
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope("profile");
      provider.addScope("email");
      const result = await signInWithPopup(auth, provider);
      return result.user ? firebaseUserToAuthUser(result.user) : null;
    } catch (e: any) {
      if (
        e.code !== "auth/popup-closed-by-user" &&
        e.code !== "auth/cancelled-popup-request"
      ) {
        console.error("[AuthProvider] Google sign-in error:", e);
        setError("Failed to sign in with Google. Please try again.");
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [auth, isNative, nativeAuth]);

  // Returns AuthUser on success, null on failure/cancellation
  const signInWithApple = useCallback(async (): Promise<AuthUser | null> => {
    if (isNative) {
      // Use native bridge for Apple SSO
      return await nativeAuth("apple");
    }

    setError(null);
    try {
      const provider = new OAuthProvider("apple.com");
      provider.addScope("email");
      provider.addScope("name");
      const result = await signInWithPopup(auth, provider);
      return result.user ? firebaseUserToAuthUser(result.user) : null;
    } catch (e: any) {
      if (
        e.code !== "auth/popup-closed-by-user" &&
        e.code !== "auth/cancelled-popup-request"
      ) {
        console.error("[AuthProvider] Apple sign-in error:", e);
        setError("Failed to sign in with Apple. Please try again.");
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [auth, isNative, nativeAuth]);

  // Returns AuthUser on success, null on failure
  const signInWithEmail = useCallback(
    async (email: string, password: string): Promise<AuthUser | null> => {
      if (isNative) {
        // Use native bridge for email sign-in (credentials stored on native)
        return await nativeAuth("email", { email, password });
      }

      setIsLoading(true);
      setError(null);
      try {
        const result = await signInWithEmailAndPassword(auth, email, password);
        return result.user ? firebaseUserToAuthUser(result.user) : null;
      } catch (e: any) {
        console.error("[AuthProvider] Email sign-in error:", e);
        if (e.code === "auth/user-not-found") {
          setError("No account found with this email. Please sign up.");
        } else if (
          e.code === "auth/wrong-password" ||
          e.code === "auth/invalid-credential"
        ) {
          setError("Incorrect password. Please try again.");
        } else if (e.code === "auth/invalid-email") {
          setError("Invalid email address.");
        } else if (e.code === "auth/too-many-requests") {
          setError("Too many failed attempts. Please try again later.");
        } else {
          setError("Failed to sign in. Please try again.");
        }
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [auth, isNative, nativeAuth]
  );

  // Sign up always uses Firebase directly (both web and native)
  // This way user data is stored in Firestore regardless of platform
  // Returns AuthUser on success, null on failure
  const signUpWithEmail = useCallback(
    async (
      email: string,
      password: string,
      firstName: string,
      lastName: string
    ): Promise<AuthUser | null> => {
      setIsLoading(true);
      setError(null);
      try {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
        // Update the user's display name
        const displayName = [firstName, lastName].filter(Boolean).join(" ");
        if (displayName) {
          await updateProfile(userCredential.user, { displayName });
        }
        sendEmailVerification(userCredential.user);
        return signInWithEmail(email, password);
      } catch (e: any) {
        console.error("[AuthProvider] Email sign-up error:", e);
        if (e.code === "auth/email-already-in-use") {
          setError(
            "An account with this email already exists. Please sign in."
          );
        } else if (e.code === "auth/weak-password") {
          setError("Password is too weak. Please use at least 6 characters.");
        } else if (e.code === "auth/invalid-email") {
          setError("Invalid email address.");
        } else {
          setError("Failed to create account. Please try again.");
        }
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [auth, signInWithEmail]
  );

  // Password reset works the same for both web and native
  const resetPasswordEmail = useCallback(
    async (email: string) => {
      setIsLoading(true);
      setError(null);
      try {
        await sendPasswordResetEmail(auth, email);
      } catch (e: any) {
        console.error("[AuthProvider] Password reset error:", e);
        if (e.code === "auth/user-not-found") {
          setError("No account found with this email address.");
        } else if (e.code === "auth/invalid-email") {
          setError("Invalid email address.");
        } else {
          setError("Failed to send reset email. Please try again.");
        }
      } finally {
        setIsLoading(false);
      }
    },
    [auth]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const showAccountDialog = useCallback(async () => {
    if (!isNative) return;

    try {
      const result = await bridge.request<{ action: string }>(
        BridgeChannels.AUTH,
        BridgeActions.SHOW_ACCOUNT_DIALOG,
        undefined,
        60000 * 5
      );
      if (result.action === "signOut") {
        await signOutFromFirebase();
      }
    } catch (e) {
      console.error("[AuthProvider] Show account dialog error:", e);
    }
  }, [isNative]);

  // Dialog control - delegate to SignInDialogContext
  const openSignIn = useCallback(
    (options?: SignInOptions) => {
      dialogContext?.open(options);
    },
    [dialogContext]
  );

  const closeSignIn = useCallback(() => {
    dialogContext?.close();
  }, [dialogContext]);

  // Auth context value
  const authValue = useMemo<AuthContextValue>(
    () => ({
      isLoaded,
      isSignedIn: !!user,
      userId: user?.uid ?? null,
      user: user ? firebaseUserToAuthUser(user) : null,
      signOut: handleSignOut,
      getToken,
      isNative,
      showAccountDialog,
      openSignIn,
      closeSignIn,
      isSignInOpen: dialogContext?.isOpen ?? false,
    }),
    [
      isLoaded,
      user,
      handleSignOut,
      getToken,
      isNative,
      showAccountDialog,
      openSignIn,
      closeSignIn,
      dialogContext?.isOpen,
    ]
  );

  // Auth actions context value (for direct auth method access)
  const actionsValue = useMemo<AuthActions>(
    () => ({
      signInWithGoogle,
      signInWithApple,
      signInWithEmail,
      signUpWithEmail,
      resetPassword: resetPasswordEmail,
      isLoading,
      error,
      clearError,
    }),
    [
      signInWithGoogle,
      signInWithApple,
      signInWithEmail,
      signUpWithEmail,
      resetPasswordEmail,
      isLoading,
      error,
      clearError,
    ]
  );

  return (
    <AuthContext.Provider value={authValue}>
      <AuthActionsContext.Provider value={actionsValue}>
        {children}
        {/* Sign-in dialog - rendered here, state in SignInDialogContext */}
        <SignInDialog />
      </AuthActionsContext.Provider>
    </AuthContext.Provider>
  );
}

// ============================================================================
// Auth Provider (wraps with SignInDialogProvider)
// ============================================================================

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  return (
    <SignInDialogProvider>
      <InnerAuthProvider>{children}</InnerAuthProvider>
    </SignInDialogProvider>
  );
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/**
 * Convenience hook to check if user is signed in
 */
export function useIsSignedIn(): boolean {
  return useAuth().isSignedIn;
}

/**
 * Convenience hook to get current user ID
 */
export function useUserId(): string | null {
  return useAuth().userId;
}

/**
 * Convenience hook to get current user
 */
export function useUser(): AuthUser | null {
  return useAuth().user;
}

/**
 * Hook to open sign-in dialog (convenience wrapper)
 */
export function useSignIn() {
  const { openSignIn, closeSignIn, isSignInOpen } = useAuth();
  return { openSignIn, closeSignIn, isSignInOpen };
}

/**
 * Hook to access auth actions directly (for special cases like Migrate page)
 */
export function useAuthActions(): AuthActions {
  const context = useContext(AuthActionsContext);
  if (!context) {
    throw new Error("useAuthActions must be used within an AuthProvider");
  }
  return context;
}
